import {hasSourceSceneGeneration,validateSourceSceneGenerationDocumentStructure,type NativeSceneGenerationContext} from '../../protocol/source-scene-generation';
import {validateSourceScriptDocument,hasSourceScript} from '../../protocol/source-script-data';
import {hasSourceBrandIntro,validateSourceBrandIntroDocument} from '../../protocol/source-brand-intro';
import {hasSourceSlides,validateSourceSlides} from '../../protocol/source-slide';
import {hasSourceText,validateSourceTextDocument} from '../../protocol/source-text';
import {hasStoryInfographics,validateCinematicMotionDocument} from '../../protocol/cinematic-motion';
import {validateCinematicMaskDocument} from '../../protocol/cinematic-masks';
import type {
  EditorState,
  TrackType,
  UndoableState,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { EditorStarterItem } from "../../vendor/src/features/remotion-editor/editor/items/item-type";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import { splitItem } from "../../vendor/src/features/remotion-editor/editor/state/actions/split-item";
import { deleteItems } from "../../vendor/src/features/remotion-editor/editor/state/actions/delete-items";
import { findAllLinkedItems } from "../../vendor/src/features/remotion-editor/editor/utils/find-linked-items";
import { itemOwner } from "./group-split";
import {hasValidTimelineAnimationWindows} from '../../protocol/animation-window';
import {hasValidPreservedGroupFrame} from '../../protocol/group-frame';
import {editorValueSignature,requiredDocumentVersion} from '../../protocol/native-envelope';
import {hasValidFadeWindows} from '../../protocol/fade-window';
import {hasValidCodeClock} from '../../protocol/code-clock';
import {hasNativeCallLaneBars,validateNativeCallLaneBars} from '../../protocol/call-lane-bar';
import { mergeTimeRanges, type TimeRange } from "./timeline-ripple";
import { insertTimelineTime } from "./timeline-insert";
import { orderTrackBlocks } from "./track-layout";
import { resolveGroupGeometry } from "../../vendor/src/features/remotion-editor/editor/items/group/group-geometry";
import { ANIMATION_META } from "../../vendor/src/features/remotion-editor/editor/items/animation/animation-meta";
import type { GroupItem } from "../../vendor/src/features/remotion-editor/editor/items/group/group-item-type";
import {preserveItemAnimationWindow} from './animation-window';

type GroupOrigin = { children: string[]; childLineages: string[]; lineageId: string; presentationHash: string };
type CutGroupProof = { fragmentId: string; signature: string; presentationHash: string; sourceFrom: number; sourceDuration: number };

/** Match a current child ID first; a split/pasted child can use its unique earlier lineage. */
function groupChildRank(
  id:string,
  currentIds:readonly string[],
  lineages:readonly string[],
  items:UndoableState["items"],
):number {
  const exact=currentIds.indexOf(id);
  if(exact>=0)return exact;
  const item=items[id];
  if(!item)return -1;
  const lineage=item.timelineSourceItemId??id;
  let found=-1;
  for(let i=0;i<lineages.length;i++)if(lineages[i]===lineage){
    if(found>=0)return -1;
    found=i;
  }
  return found;
}
function followsGroupPaintOrder(group:GroupItem,origin:GroupOrigin,items:UndoableState["items"]):boolean {
  let previousRank=-1,previousFrom=-1,previousEnd=-1;
  for(const id of group.children){
    const item=items[id],rank=groupChildRank(id,origin.children,origin.childLineages,items);
    if(!item||rank<0||rank<previousRank||
      rank===previousRank&&(item.from<previousFrom||item.from<previousEnd))return false;
    previousRank=rank;previousFrom=item.from;previousEnd=item.from+item.durationInFrames;
  }
  return true;
}

function hashText(value:string):string {
  let first=2166136261,second=0x9e3779b9;
  for(let i=0;i<value.length;i++){
    const code=value.charCodeAt(i);
    first=Math.imul(first^code,16777619);
    second=Math.imul(second^code,2246822519);
  }
  return `${value.length.toString(36)}:${(first>>>0).toString(16)}:${(second>>>0).toString(16)}`;
}
function canonicalHash(value:unknown):string {
  try{return hashText(editorValueSignature(value));}
  catch{return '';} // Invalid/oversized proof input fails the parser's hash check.
}
function groupPresentationHash(group:GroupItem,items:UndoableState["items"]):string {
  const {id,timelineSourceItemId,children,...fields}=group;
  return canonicalHash({...fields,...resolveGroupGeometry(group,items),preserveGroupFrame:true});
}
function groupSubtreeHash(state:EditorState,id:string):string|null {
  const seen=new Set<string>(),values:unknown[]=[];
  const visit=(key:string):boolean=>{
    if(seen.has(key))return false;
    seen.add(key);
    const item=state.undoableState.items[key];
    if(!item)return false;
    values.push(item);
    if('assetId' in item)values.push(state.undoableState.assets[item.assetId]);
    if(item.type==='group')return item.children.every(visit);
    return true;
  };
  return visit(id)?canonicalHash(values):null;
}

export type ClipboardGraph = {
  sourceSceneGeneration?:NativeSceneGenerationContext;
  compositionWidth?:number;
  compositionHeight?:number;
  format: "waka-editor-clipboard";
  version: 1|2|3|4|5|6|7|8|9|10;
  fps: number;
  roots: string[];
  items: UndoableState["items"];
  assets: UndoableState["assets"];
  tracks: TrackType[];
  transitions: UndoableState["transitions"];
  /** Only mixed nested-member/main captures use the main slice for timeline insertion. */
  mixedMainInsertionRange?: { start: number; end: number };
  mixedGroupOrigins?: Record<string,GroupOrigin>;
  /** Cut-only proof for exact, unedited retained fragments; never stored on document items. */
  mixedCutGroupProof?: Record<string,CutGroupProof[]>;
};
const idOk = (id: unknown): id is string =>
  typeof id === "string" &&
  id.length > 0 &&
  id.length <= 512 &&
  !["__proto__", "constructor", "prototype"].includes(id);
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const number = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);
const frame = (v: unknown): v is number =>
  number(v) && Number.isSafeInteger(v) && v >= 0;
const ids = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every(idOk) && new Set(v).size === v.length;
const nativeTypes = new Set([
  "video",
  "image",
  "audio",
  "captions",
  "text",
  "solid",
  "blur",
  "gif",
  "webp",
  "pdf",
  "ppt",
  "group",
  "code",
]);
export const requiredClipboardVersion = (document: unknown): 1|2|3|4|5|6|7|9|10 =>
  (hasSourceSceneGeneration(document)||hasSourceScript(document)||hasSourceBrandIntro(document)||hasSourceSlides(document)||hasSourceText(document)||hasStoryInfographics(document)) ? 10 : hasNativeCallLaneBars(document) ? 9 : requiredDocumentVersion(document) as 1|2|3|4|5|6|7;
const urlOk = (v: unknown) =>
  v === null ||
  (typeof v === "string" &&
    (/^(https?:|blob:)/.test(v) || /^data:(image|audio|video)\//.test(v)));
function safeJson(v: unknown, depth = 0, budget = { left: 500000 }): boolean {
  if (depth > 64 || --budget.left < 0) return false;
  if (v === null || typeof v === "boolean") return true;
  if (typeof v === "string") return v.length < 4_000_000;
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v))
    return v.every((child) => safeJson(child, depth + 1, budget));
  return (
    record(v) &&
    Object.entries(v).every(
      ([key, child]) => idOk(key) && safeJson(child, depth + 1, budget),
    )
  );
}

/** Validate the portable graph boundary before it can change a document. Optional native presentation fields remain intact. */
export function parseClipboardGraph(
  value: unknown,
  options: { allowEmpty?: boolean } = {},
): ClipboardGraph | null {
  if (
    !record(value) ||
    value.format !== "waka-editor-clipboard" ||
    (value.version !== 1&&value.version!==2&&value.version!==3&&value.version!==4&&value.version!==5&&value.version!==6&&value.version!==7&&value.version!==8&&value.version!==9&&value.version!==10) ||
    !number(value.fps) ||
    value.fps <= 0 ||
    value.fps > 240 ||
    !ids(value.roots) ||
    (!options.allowEmpty && !value.roots.length) ||
    !record(value.items) ||
    !record(value.assets) ||
    !record(value.transitions) ||
    !Array.isArray(value.tracks) ||
    !safeJson(value)
  )
    return null;
  const { items, assets, tracks, roots, transitions } = value;
  if(Number(value.version)<requiredClipboardVersion({items,assets})||!validateSourceScriptDocument({items})||!validateSourceBrandIntroDocument({items,assets})||!validateSourceSlides({items,assets})||!validateSourceTextDocument({items})||!validateCinematicMaskDocument({items})||!validateCinematicMotionDocument({items})||!validateNativeCallLaneBars({items}))return null;
  if (Object.keys(items).length > 10000 || tracks.length > 1000) return null;
  for (const [id, item] of Object.entries(items)) {
    if (
      !record(item) ||
      item.id !== id ||
      !idOk(id) ||
      typeof item.type !== "string" ||
      !nativeTypes.has(item.type) ||
      !frame(item.from) ||
      !frame(item.durationInFrames) ||
      item.durationInFrames < 1 ||
      !["top", "left", "width", "height", "opacity"].every((key) =>
        number(item[key]),
      ) ||
      Number(item.width) < 0 ||
      Number(item.height) < 0
    )
      return null;
    for (const key of [
      "renderMeta",
      "sourceTypography",
      "shadow",
      "background",
      "captionEdgeHold",
    ])
      if (item[key] !== undefined && item[key] !== null && !record(item[key]))
        return null;
    for (const key of [
      "segments",
      "sourceTitleSegments",
      "translatedTitleSegments",
    ])
      if (
        item[key] !== undefined &&
        (!Array.isArray(item[key]) ||
          !item[key].every(
            (segment) => record(segment) && typeof segment.text === "string",
          ))
      )
        return null;
    for (const [key, fields] of [
      ["positionKeyframes", ["dx", "dy"]],
      ["zoomKeyframes", ["left", "top", "size"]],
      ["pageKeyframes", ["pageIndex"]],
    ] as const) {
      const points = item[key];
      if (
        points !== undefined &&
        (!Array.isArray(points) ||
          !points.every(
            (point) =>
              record(point) &&
              Number.isSafeInteger(point.frame) &&
              fields.every((field) => number(point[field])),
          ))
      )
        return null;
    }
    if(!hasValidTimelineAnimationWindows(item,value.fps))return null;
    if(!hasValidPreservedGroupFrame(item))return null;
    if(!hasValidFadeWindows(item,value.fps))return null;
    if(!hasValidCodeClock(item,value.fps))return null;
    if (
      item.animations !== undefined &&
      (!Array.isArray(item.animations) ||
        !item.animations.every(
          (a) =>
            record(a) &&
            typeof a.type === "string" &&
            Object.hasOwn(ANIMATION_META, a.type) &&
            number(a.durationInSeconds) &&
            a.durationInSeconds >= 0,
        ))
    )
      return null;
    if (item.type === "group") {
      if (!ids(item.children) || (!item.children.length && item.preserveGroupFrame !== true)) return null;
    } else if ("assetId" in item) {
      if (!idOk(item.assetId)) return null;
      const asset = assets[item.assetId];
      if (
        !record(asset) ||
        asset.type !==
          (item.type === "captions"
            ? "caption"
            : item.type === "ppt"
              ? "pdf"
              : item.type)
      )
        return null;
      if(item.type==='pdf'||item.type==='ppt'){
        const pages=asset.numPages;
        if(!number(pages)||!Number.isSafeInteger(pages)||pages<1)return null;
        const validPage=(page:unknown)=>number(page)&&Number.isSafeInteger(page)&&page>=0&&page<pages;
        if(!validPage(item.pageIndex))return null;
        if(item.pageKeyframes!==undefined&&(!Array.isArray(item.pageKeyframes)||!item.pageKeyframes.every(point=>record(point)&&validPage(point.pageIndex))))return null;
        if(item.pageTransitionDurationInSeconds!==undefined&&(!number(item.pageTransitionDurationInSeconds)||item.pageTransitionDurationInSeconds<0))return null;
      }
    } else if (
      [
        "video",
        "image",
        "audio",
        "captions",
        "gif",
        "webp",
        "pdf",
        "ppt",
      ].includes(item.type)
    )
      return null;
    if (
      ["captions", "text"].includes(item.type) &&
      (!["fontFamily", "color", "align", "direction", "strokeColor"].every(
        (key) => typeof item[key] === "string",
      ) ||
        !["fontSize", "lineHeight", "letterSpacing", "strokeWidth"].every(
          (key) => number(item[key]),
        ) ||
        !record(item.fontStyle) ||
        typeof item.fontStyle.weight !== "string" ||
        typeof item.fontStyle.variant !== "string")
    )
      return null;
    if (
      (item.type === "text" && typeof item.text !== "string") ||
      (item.type === "code" && typeof item.code !== "string")
    )
      return null;
    if (
      item.type === "captions" &&
      (!number(item.captionStartInSeconds) ||
        !number(item.pageDurationInMilliseconds) ||
        !number(item.maxLines))
    )
      return null;
    if (
      ["video", "audio", "gif", "webp"].includes(item.type) &&
      (!number(item.playbackRate) ||
        item.playbackRate <= 0 ||
        !number(item[`${item.type}StartFromInSeconds`]))
    )
      return null;
  }
  for (const [id, asset] of Object.entries(assets)) {
    if (
      !record(asset) ||
      asset.id !== id ||
      !idOk(id) ||
      !["image", "video", "audio", "caption", "gif", "webp", "pdf"].includes(
        String(asset.type),
      ) ||
      typeof asset.filename !== "string" ||
      typeof asset.mimeType !== "string" ||
      !number(asset.size) ||
      asset.size < 0 ||
      !urlOk(asset.remoteUrl)
    )
      return null;
    if(asset.type==='pdf'&&(!number(asset.numPages)||!Number.isSafeInteger(asset.numPages)||asset.numPages<1||!number(asset.pageWidth)||asset.pageWidth<=0||!number(asset.pageHeight)||asset.pageHeight<=0))return null;
    if (asset.type === "caption")
      for (const key of ["captions", "sourceCaptions", "translatedCaptions"]) {
        const cues = asset[key];
        if (cues === undefined && key !== "captions") continue;
        if (
          !Array.isArray(cues) ||
          !cues.every(
            (cue) =>
              record(cue) &&
              typeof cue.text === "string" &&
              number(cue.startMs) &&
              number(cue.endMs) &&
              cue.endMs >= cue.startMs,
          )
        )
          return null;
      }
  }
  const counts = new Map<string, number>();
  const visit = (id: string, path: Set<string>): boolean => {
    if (path.has(id) || !record(items[id])) return false;
    counts.set(id, (counts.get(id) ?? 0) + 1);
    if (counts.get(id) !== 1) return false;
    const item = items[id];
    return (
      item.type !== "group" ||
      (item.children as string[]).every((child) =>
        visit(child, new Set(path).add(id)),
      )
    );
  };
  if (
    !roots.every((id) => visit(id, new Set())) ||
    counts.size !== Object.keys(items).length
  )
    return null;
  const tracked: string[] = [];
  const trackIds = new Set<string>();
  for (const track of tracks) {
    if (
      !record(track) ||
      !idOk(track.id) ||
      trackIds.has(track.id) ||
      !ids(track.items) ||
      !["visual", "audio"].includes(String(track.zone)) ||
      !["locked", "muted", "hidden", "magnetic"].every(
        (key) => typeof track[key] === "boolean",
      )
    )
      return null;
    trackIds.add(track.id);
    tracked.push(...track.items);
  }
  if (
    tracked.length !== roots.length ||
    new Set(tracked).size !== tracked.length ||
    tracked.some((id) => !roots.includes(id))
  )
    return null;
  for (const [id, t] of Object.entries(transitions)) {
    if (
      !record(t) ||
      t.id !== id ||
      !idOk(t.sourceItemId) ||
      !idOk(t.targetItemId) ||
      !items[t.sourceItemId] ||
      !items[t.targetItemId] ||
      !idOk(t.trackId) ||
      !trackIds.has(t.trackId) ||
      !Number.isSafeInteger(t.from) ||
      !frame(t.durationInFrames) ||
      t.durationInFrames < 1 ||
      typeof t.transitionType !== "string"
    )
      return null;
  }
  // Mixed clipboard version 8 is deliberately separate from native document
  // versions (<=7), so older editors reject the new paste semantics.
  const hasMixedFields=value.mixedMainInsertionRange!==undefined||value.mixedGroupOrigins!==undefined||value.mixedCutGroupProof!==undefined;
  if(value.version===8||((value.version===9||value.version===10)&&hasMixedFields)){
    if(!record(value.mixedMainInsertionRange)||!record(value.mixedGroupOrigins)||!Object.keys(value.mixedGroupOrigins).length)return null;
    for(const [id,origin] of Object.entries(value.mixedGroupOrigins)){
      if(!idOk(id)||!record(origin)||!ids(origin.children)||!origin.children.length||
        !Array.isArray(origin.childLineages)||origin.childLineages.length!==origin.children.length||
        !origin.childLineages.every(idOk)||!idOk(origin.lineageId)||
        typeof origin.presentationHash!=="string"||!/^[-a-z0-9:]+$/.test(origin.presentationHash))return null;
      const copies=Object.values(items).filter((item)=>record(item)&&item.type==="group"&&(item.id===id||item.timelineSourceItemId===origin.lineageId));
      if(copies.length!==1||groupPresentationHash(copies[0] as GroupItem,items as UndoableState["items"])!==origin.presentationHash)return null;
      const copiedGroup=copies[0] as GroupItem;
      if(!followsGroupPaintOrder(copiedGroup,origin as GroupOrigin,items as UndoableState["items"]))return null;
    }
    if(value.mixedCutGroupProof!==undefined){
      if(!record(value.mixedCutGroupProof))return null;
      for(const [parentId,origin] of Object.entries(value.mixedGroupOrigins))
        for(const childId of (origin as GroupOrigin).children)
          if(Object.hasOwn(value.mixedCutGroupProof,childId)&&!Object.hasOwn(value.mixedCutGroupProof,parentId))return null;
      for(const [id,proofs] of Object.entries(value.mixedCutGroupProof)){
        if(!Object.hasOwn(value.mixedGroupOrigins,id)||!Array.isArray(proofs)||!proofs.length)return null;
        const seenFragments=new Set<string>();
        const copied=Object.values(items).find((item)=>record(item)&&item.type==="group"&&
          (item.id===id||item.timelineSourceItemId===(value.mixedGroupOrigins as Record<string,GroupOrigin>)[id].lineageId));
        if(!record(copied))return null;
        let previousSourceEnd=-1;
        for(const proof of proofs){
          if(!record(proof)||!idOk(proof.fragmentId)||seenFragments.has(proof.fragmentId)||
            typeof proof.signature!=="string"||!/^[-a-z0-9:]+$/.test(proof.signature)||
            typeof proof.presentationHash!=="string"||
            proof.presentationHash!==(value.mixedGroupOrigins[id] as GroupOrigin).presentationHash||
            !frame(proof.sourceFrom)||!frame(proof.sourceDuration)||proof.sourceDuration<1||
            proof.sourceFrom<previousSourceEnd||
            proof.sourceFrom<Number(copied.from)||proof.sourceFrom+proof.sourceDuration>Number(copied.from)+Number(copied.durationInFrames))return null;
          seenFragments.add(proof.fragmentId);
          previousSourceEnd=proof.sourceFrom+proof.sourceDuration;
        }
      }
    }
  }else if(value.mixedMainInsertionRange!==undefined||value.mixedGroupOrigins!==undefined||value.mixedCutGroupProof!==undefined)return null;
  if (value.mixedMainInsertionRange !== undefined) {
    const range = value.mixedMainInsertionRange;
    if (!record(range) || !frame(range.start) || !frame(range.end) || range.end <= range.start)
      return null;
    const mainItems = tracks
      .filter((track) =>
        record(track) && track.magnetic === true && record(track.metadata) &&
        track.metadata.isMainTrack === true && track.metadata.type === "video",
      )
      .flatMap((track) => (track as TrackType).items)
      .map((id) => items[id]);
    if (!mainItems.length || mainItems.some((item) => !record(item))) return null;
    const validMainItems = mainItems.filter(record);
    if (range.start !== Math.min(...validMainItems.map((item) => Number(item.from))) ||
      range.end !== Math.max(...validMainItems.map((item) => Number(item.from) + Number(item.durationInFrames))))
      return null;
  }
  if(!validateSourceSceneGenerationDocumentStructure({...value,compositionWidth:value.compositionWidth??1,compositionHeight:value.compositionHeight??1}))return null;
  // The required native geometry, media clocks, asset kinds and graph ownership have been checked above.
  return value as ClipboardGraph;
}

/** A selected ancestor already carries its member; only uncovered nested roots need detaching. */
export function mixedLinkedNestedSelection(
  state: EditorState,
  selected: readonly string[],
  linked = state.isLinkingEnabled,
): string[] {
  if (!linked) return [];
  const requested = [...new Set(selected)].filter((id) => state.undoableState.items[id] && itemOwner(state, id));
  const mains = requested.some((id) => {
    const owner = itemOwner(state, id);
    return owner && !owner.parentId && !owner.track.locked && owner.track.magnetic &&
      owner.track.metadata?.isMainTrack && owner.track.metadata.type === "video";
  });
  if (!mains) return [];
  const chosen = new Set(requested);
  return requested.filter((id) => {
    let owner = itemOwner(state, id);
    if (!owner?.parentId) return false;
    while (owner?.parentId) {
      if (chosen.has(owner.parentId) && !itemOwner(state, owner.parentId)?.parentId) return false;
      owner = itemOwner(state, owner.parentId);
    }
    return true;
  });
}

function generationClipboardMetadata(document:UndoableState,items:UndoableState['items'],assets:UndoableState['assets']){
  if(!hasSourceSceneGeneration({items,assets}))return {};
  const urls=new Set<string>(),names=new Set<string>();
  for(const item of Object.values(items)){
    const meta=item.sourceSceneSpec,capsule=meta?.capsule;
    for(const descriptor of meta?.sourceReferences??[])if(descriptor.assetId){const bound=document.assets[descriptor.assetId];if(bound)assets[bound.id]=structuredClone(bound);if(['character','identity'].includes(descriptor.reference.role??''))for(const cast of document.sourceSceneGeneration?.cast??[])if(cast.mainAssetId===descriptor.assetId)names.add(cast.name);}
    for(const url of [capsule?.startAnchor?.url,capsule?.locationRef?.url,capsule?.aiRef?.url,capsule?.consistency?.anchorUrl,meta?.regenFields?.consistency?.anchorUrl])if(url)urls.add(url);
    if(capsule?.startAnchor?.castName)names.add(capsule.startAnchor.castName);
  }
  const cast=document.sourceSceneGeneration?.cast.filter(value=>names.has(value.name))??[],ids=new Set(cast.map(value=>value.mainAssetId));
  for(const asset of Object.values(document.assets))if(asset.type==='image'&&(ids.has(asset.id)||!!asset.remoteUrl&&urls.has(asset.remoteUrl)))assets[asset.id]=structuredClone(asset);
  return {compositionWidth:document.compositionWidth,compositionHeight:document.compositionHeight,...(cast.length?{sourceSceneGeneration:{version:1 as const,cast:structuredClone(cast)}}:{})};
}
function captureMixedClipboardGraph(
  state: EditorState,
  requested: string[],
  nested: string[],
): ClipboardGraph | null {
  const explicit = captureClipboardGraphInner(state, nested, false);
  if (!explicit) return null;
  const withoutNested = deleteItems(state, nested, { isLinkingEnabled: false, timingApplied: true, internalDetach:true });
  if (withoutNested === state) return null;
  const linked = captureClipboardGraphInner(withoutNested, requested.filter((id) => !nested.includes(id)), true);
  if (!linked || linked.fps !== explicit.fps) return null;
  const mainRoots = linked.tracks
    .filter((track) => track.magnetic && track.metadata?.isMainTrack && track.metadata.type === "video")
    .flatMap((track) => track.items.map((id) => linked.items[id]!));
  if (!mainRoots.length) return null;
  const items = { ...linked.items };
  for (const [id, item] of Object.entries(explicit.items)) {
    if (items[id]) return null;
    items[id] = item;
  }
  const assets = { ...linked.assets };
  for (const [id, asset] of Object.entries(explicit.assets)) {
    if (assets[id] && JSON.stringify(assets[id]) !== JSON.stringify(asset)) return null;
    assets[id] = asset;
  }
  const transitions = { ...linked.transitions };
  for (const [id, transition] of Object.entries(explicit.transitions)) {
    if (transitions[id]) return null;
    transitions[id] = transition;
  }
  const linkedTracks=linked.tracks.map((track)=>({...track,items:[...track.items]}));
  // Rebuild the original group envelope around every explicit member. Keeping a
  // member on a separate track changes child z-order and applies group opacity twice.
  const newGroupRoots = new Map<string, string>();
  const mixedGroupOrigins:Record<string,GroupOrigin>={};
  for (const selectedId of explicit.roots) {
    let childId = selectedId;
    let owner = itemOwner(state, selectedId);
    while (owner?.parentId) {
      const sourceId = owner.parentId;
      const source = state.undoableState.items[sourceId];
      if (source?.type !== "group") return null;
      if(source.children.some((id)=>!state.undoableState.items[id]))return null;
      const lineageId=source.timelineSourceItemId??source.id;
      mixedGroupOrigins[sourceId]={
        children:[...source.children],
        childLineages:source.children.map((id)=>state.undoableState.items[id]!.timelineSourceItemId??id),
        lineageId,
        presentationHash:groupPresentationHash(source,state.undoableState.items),
      };
      const candidates = Object.values(items).filter((item):item is GroupItem =>
        item.type === "group" && (item.id === sourceId || item.timelineSourceItemId === lineageId),
      ).sort((a,b)=>a.from-b.from);
      for(let i=1;i<candidates.length;i++)
        if(candidates[i-1].from+candidates[i-1].durationInFrames>candidates[i].from)return null;
      const targetId = candidates[0]?.id ?? sourceId;
      if (!candidates.length && items[targetId]) return null;
      const priorChildren = candidates.flatMap((candidate)=>candidate.children);
      for(const redundant of candidates.slice(1)){
        if(Object.values(transitions).some((transition)=>
          transition.sourceItemId===redundant.id||transition.targetItemId===redundant.id))return null;
        for(const [id,item] of Object.entries(items))if(id!==redundant.id&&item.type==="group"&&item.children.includes(redundant.id))
          items[id]={...item,children:[...new Set(item.children.map((child)=>child===redundant.id?targetId:child))]};
        for(const track of linkedTracks)track.items=track.items.filter((id)=>id!==redundant.id);
        delete items[redundant.id];
      }
      const childIds = [...new Set([...priorChildren, childId])];
      const rank = (id: string) => groupChildRank(id,source.children,mixedGroupOrigins[sourceId]!.childLineages,items);
      if (childIds.some((id) => rank(id) < 0)) return null;
      childIds.sort((a, b) => rank(a) - rank(b)||items[a]!.from-items[b]!.from);
      for(let i=0;i<childIds.length;i++)for(let j=i+1;j<childIds.length;j++){
        const a=items[childIds[i]]!,b=items[childIds[j]]!;
        if(rank(a.id)===rank(b.id)&&a.from<b.from+b.durationInFrames&&b.from<a.from+a.durationInFrames)return null;
      }
      items[targetId] = {
        ...structuredClone(source),
        ...resolveGroupGeometry(source, state.undoableState.items),
        id: targetId,
        timelineSourceItemId: source.timelineSourceItemId ?? source.id,
        preserveGroupFrame: true,
        children: childIds,
      };
      childId = targetId;
      owner = itemOwner(state, sourceId);
    }
    if (!owner) return null;
    if (!linkedTracks.some((track)=>track.items.includes(childId))) newGroupRoots.set(childId, owner.track.id);
  }
  const usedTrackIds = new Set([
    ...state.undoableState.tracks.map((track) => track.id),
    ...linkedTracks.map((track) => track.id),
  ]);
  const detached = [...newGroupRoots].map(([id, ownerId]) => {
    const owner = state.undoableState.tracks.find((track) => track.id === ownerId);
    if (!owner) return null;
    const seed = `clipboard-member-${generateRandomId(12)}`;
    let trackId = seed, serial = 0;
    while (usedTrackIds.has(trackId)) trackId = `${seed}-${++serial}`;
    usedTrackIds.add(trackId);
    return {
      ownerId,
      track: {
        ...structuredClone(owner),
        id: trackId,
        items: [id],
        locked: false,
        magnetic: false,
        metadata: owner.metadata
          ? { ...owner.metadata, isMainTrack: false, trackType: null, kind: undefined }
          : undefined,
      } satisfies TrackType,
    };
  });
  if (detached.some((entry) => !entry)) return null;
  const tracks = state.undoableState.tracks.flatMap((originalTrack) => [
    ...linkedTracks.filter((track) => track.id === originalTrack.id),
    ...detached.filter((entry) => entry?.ownerId === originalTrack.id).map((entry) => entry!.track),
  ]);
  const roots = tracks.flatMap((track) => track.items);
  const graph: ClipboardGraph = {
    ...generationClipboardMetadata(state.undoableState,items,assets),
    format: "waka-editor-clipboard",
    version: Math.max(8,requiredClipboardVersion({items,assets})) as ClipboardGraph["version"],
    fps: state.undoableState.fps,
    roots,
    items,
    assets,
    tracks,
    transitions,
    mixedMainInsertionRange: {
      start: Math.min(...mainRoots.map((item) => item.from)),
      end: Math.max(...mainRoots.map((item) => item.from + item.durationInFrames)),
    },
    mixedGroupOrigins,
  };
  return parseClipboardGraph(JSON.parse(JSON.stringify(graph)));
}

function captureClipboardGraphInner(
  state: EditorState,
  selected: readonly string[],
  linked = state.isLinkingEnabled,
): ClipboardGraph | null {
  const requested = [...new Set(selected)].filter(
    (id) => state.undoableState.items[id] && itemOwner(state, id),
  );
  if (!requested.length) return null;
  const detached = mixedLinkedNestedSelection(state, requested, linked);
  if (detached.length) return captureMixedClipboardGraph(state, requested, detached);
  let prepared = state;
  let roots = linked
    ? findAllLinkedItems({ targetItemIds: requested, ...state.undoableState })
    : requested;
  const mains = requested.filter((id) => {
    const own = itemOwner(state, id);
    return (
      own &&
      !own.parentId &&
      !own.track.locked &&
      own.track.magnetic &&
      own.track.metadata?.isMainTrack &&
      own.track.metadata.type === "video"
    );
  });
  if (linked && mains.length) {
    const ranges = mergeTimeRanges(
      mains.map((id) => {
        const i = state.undoableState.items[id]!;
        return { start: i.from, end: i.from + i.durationInFrames };
      }),
    );
    for (const boundary of [
      ...new Set(ranges.flatMap((r) => [r.start, r.end])),
    ].sort((a, b) => a - b))
      for (const track of prepared.undoableState.tracks) {
        if (track.locked) continue;
        for (const id of [...track.items]) {
          const i = prepared.undoableState.items[id]!;
          if (
            !requested.includes(id) &&
            i.from < boundary &&
            i.from + i.durationInFrames > boundary
          ) {
            const next = splitItem({
              state: prepared,
              idToSplit: id,
              framePosition: boundary,
            });
            if (next === prepared) return null;
            prepared = next;
          }
        }
      }
    roots = [
      ...new Set([
        ...requested.filter((id) => prepared.undoableState.items[id]),
        ...prepared.undoableState.tracks
          .filter((t) => !t.locked)
          .flatMap((t) =>
            t.items.filter((id) => {
              const i = prepared.undoableState.items[id]!;
              return ranges.some(
                (r) =>
                  i.from >= r.start && i.from + i.durationInFrames <= r.end,
              );
            }),
          ),
      ]),
    ];
  }
  roots = roots.filter((id) => {
    let own = itemOwner(prepared, id);
    while (own?.parentId) {
      if (roots.includes(own.parentId)) return false;
      own = itemOwner(prepared, own.parentId);
    }
    return true;
  });
  // Selection order is not paint order (notably after Undo or additive selection).
  const paintOrder = new Map<string, number>();
  const indexTree = (id: string) => {
    if (paintOrder.has(id)) return;
    paintOrder.set(id, paintOrder.size);
    const item = prepared.undoableState.items[id];
    if (item?.type === "group") item.children.forEach(indexTree);
  };
  prepared.undoableState.tracks.forEach((track) =>
    track.items.forEach(indexTree),
  );
  roots.sort((a, b) => paintOrder.get(a)! - paintOrder.get(b)!);
  const items: UndoableState["items"] = {},
    assets: UndoableState["assets"] = {};
  const visit = (id: string, path = new Set<string>()): boolean => {
    if (path.has(id) || items[id]) return false;
    const item = prepared.undoableState.items[id];
    if (!item) return false;
    items[id] = structuredClone(item);
    delete items[id].sourceSpeechRestore;
    if(items[id].sourceBrandIntro)delete items[id].sourceBrandIntro!.leadingInterval;
    if ("assetId" in item) {
      const asset = prepared.undoableState.assets[item.assetId];
      if (!asset) return false;
      assets[asset.id] = structuredClone(asset);
    }
    if (item.type !== "group") return true;
    if (!item.children.every((child) => visit(child, new Set(path).add(id))))
      return false;
    items[id] = {
      ...item,
      ...resolveGroupGeometry(item, items),
      children: [...item.children],
    };
    return true;
  };
  if (!roots.every((id) => visit(id))) return null;
  const tracks = prepared.undoableState.tracks.flatMap((track) => {
    const selectedRoots = roots.filter(
      (id) => itemOwner(prepared, id)?.track.id === track.id,
    );
    return selectedRoots.length
      ? [{ ...structuredClone(track), items: selectedRoots }]
      : [];
  });
  const transitions = Object.fromEntries(
    Object.entries(prepared.undoableState.transitions)
      .filter(([, t]) => items[t.sourceItemId] && items[t.targetItemId])
      .map(([id, t]) => [id, structuredClone(t)]),
  );
  // JSON roundtrip strips optional undefined fields, exactly as the system clipboard does.
  return parseClipboardGraph(
    JSON.parse(
      JSON.stringify({
        ...generationClipboardMetadata(state.undoableState,items,assets),
        format: "waka-editor-clipboard",
        version: requiredClipboardVersion({items,assets}),
        fps: state.undoableState.fps,
        roots,
        items,
        assets,
        tracks,
        transitions,
      }),
    ),
  );
}

export function captureClipboardGraph(
  state: EditorState,
  selected: readonly string[],
  linked = state.isLinkingEnabled,
): ClipboardGraph | null {
  try {
    return captureClipboardGraphInner(state, selected, linked);
  } catch {
    return null;
  }
}

function selectedMainRanges(graph:ClipboardGraph):TimeRange[] {
  return mergeTimeRanges(graph.tracks
    .filter((track)=>track.magnetic&&track.metadata?.isMainTrack&&track.metadata.type==="video")
    .flatMap((track)=>track.items.map((id)=>({
      start:graph.items[id]!.from,
      end:graph.items[id]!.from+graph.items[id]!.durationInFrames,
    }))));
}
function sourceFrameAfterCut(frame:number,ranges:readonly TimeRange[]):number {
  let sourceFrame=frame;
  for(const range of ranges)if(sourceFrame>=range.start)sourceFrame+=range.end-range.start;
  return sourceFrame;
}
function groupContentOverlaps(
  id:string,items:UndoableState["items"],start:number,end:number,seen=new Set<string>(),
):boolean {
  if(seen.has(id))return false;
  const item=items[id];
  if(!item)return false;
  if(item.type==="group"){
    const path=new Set(seen).add(id);
    return item.children.some((child)=>groupContentOverlaps(child,items,start,end,path));
  }
  return item.type!=="audio"&&item.from<end&&start<item.from+item.durationInFrames;
}

/** Bind a mixed cut to its exact retained group fragments without modifying the document. */
export function bindMixedCutGroupProof(
  source: EditorState,
  cut: EditorState,
  graph: ClipboardGraph,
): ClipboardGraph | null {
  if((graph.version!==8&&graph.version!==9&&graph.version!==10)||!graph.mixedGroupOrigins)return null;
  const proof:Record<string,CutGroupProof[]>={};
  let removedRanges:TimeRange[];
  try{removedRanges=selectedMainRanges(graph);}catch{return null;}
  if(!removedRanges.length)return null;
  for(const [id,origin] of Object.entries(graph.mixedGroupOrigins)){
    const sourceGroup=source.undoableState.items[id];
    if(sourceGroup?.type!=="group"||groupPresentationHash(sourceGroup,source.undoableState.items)!==origin.presentationHash)return null;
    const sourceOwners=Object.values(source.undoableState.items).filter((item)=>
      item.type==="group"&&(item.id===id||item.timelineSourceItemId===origin.lineageId));
    if(sourceOwners.length!==1||sourceOwners[0].id!==id)return null;
    const fragments=Object.values(cut.undoableState.items).filter((item)=>
      item.type==="group"&&(item.id===id||item.timelineSourceItemId===origin.lineageId))
      .sort((a,b)=>a.from-b.from);
    if(!fragments.length)continue;
    const sourceBounds=resolveGroupGeometry(sourceGroup,source.undoableState.items);
    const entries:CutGroupProof[]=[];
    for(let i=0;i<fragments.length;i++){
      const fragment=fragments[i];
      if(i&&fragments[i-1].from+fragments[i-1].durationInFrames>fragment.from)return null;
      const signature=groupSubtreeHash(cut,fragment.id);
      const sourceFrom=sourceFrameAfterCut(fragment.from,removedRanges);
      const sourceEnd=sourceFrameAfterCut(fragment.from+fragment.durationInFrames-1,removedRanges)+1;
      if(!signature||!Number.isSafeInteger(sourceEnd)||sourceEnd-sourceFrom!==fragment.durationInFrames||
        sourceFrom<sourceBounds.from||sourceEnd>sourceBounds.from+sourceBounds.durationInFrames)return null;
      entries.push({fragmentId:fragment.id,signature,presentationHash:origin.presentationHash,
        sourceFrom,sourceDuration:fragment.durationInFrames});
    }
    proof[id]=entries;
  }
  return parseClipboardGraph({...graph,mixedCutGroupProof:proof});
}

function compatibleRetainedPresentation(
  captured:GroupItem,retained:GroupItem,sourceFrom:number,fps:number,
):boolean {
  const offset=sourceFrom-captured.from;
  const expected=preserveItemAnimationWindow(captured,fps,offset,captured.durationInFrames);
  const expectedPoints=captured.positionKeyframes?.map((point)=>({...point,frame:point.frame-offset}));
  const presentation=(group:GroupItem)=>{
    const {id,children,from,durationInFrames,timelineSourceItemId,...fields}=group;
    return fields;
  };
  try{return editorValueSignature(presentation({...expected,positionKeyframes:expectedPoints}))===
    editorValueSignature(presentation(retained));}
  catch{return false;}
}

function hasUniqueItemOwnership(items:UndoableState["items"],tracks:TrackType[],required:readonly string[]):boolean {
  const seen=new Set<string>();
  const visit=(id:string):boolean=>{
    if(seen.has(id)||!items[id])return false;
    seen.add(id);
    const item=items[id];
    return item.type!=="group"||item.children.every(visit);
  };
  return tracks.every((track)=>track.items.every(visit))&&required.every((id)=>seen.has(id));
}

function coalesceProvenCutGroups(
  before:EditorState,
  graph:ClipboardGraph,
  itemIds:Map<string,string>,
  items:UndoableState["items"],
  tracks:TrackType[],
  transitions:UndoableState["transitions"],
  insertionAt:number,
  insertionDuration:number,
  pasteOffset:number,
):boolean {
  const proof=graph.mixedCutGroupProof,origins=graph.mixedGroupOrigins;
  if(!proof||!Object.keys(proof).length)return true;
  if(!origins)return false;
  const proofEntries=Object.entries(proof).flatMap(([originId,entries])=>entries.map((entry)=>({originId,entry})));
  const present=proofEntries.filter(({entry})=>before.undoableState.items[entry.fragmentId]);
  if(!present.length)return true; // Pasting into another document is an independent copy.
  if(present.length!==proofEntries.length)return false;
  // Verify every retained fragment against the exact state left by cut BEFORE
  // insertion moves its clock. An edit, unrelated document, or ambiguous split
  // prevents coalescing rather than merging two unrelated groups.
  for(const [originId,original] of Object.entries(origins)){
    const entries=proof[originId]??[];
    const matching=Object.values(before.undoableState.items).filter((item)=>
      item.type==="group"&&(item.id===originId||item.timelineSourceItemId===original.lineageId));
    if(matching.length!==entries.length||
      matching.some((item)=>!entries.some((entry)=>entry.fragmentId===item.id)))return false;
    for(const entry of entries){
      const fragment=before.undoableState.items[entry.fragmentId];
      if(fragment?.type!=="group"||
        (fragment.id!==originId&&fragment.timelineSourceItemId!==original.lineageId)||
        entry.presentationHash!==original.presentationHash||
        groupSubtreeHash(before,entry.fragmentId)!==entry.signature||
        !followsGroupPaintOrder(fragment,original,before.undoableState.items))return false;
    }
  }
  const depth=(id:string,seen=new Set<string>()):number|null=>{
    if(seen.has(id)||seen.size>64)return null;
    seen.add(id);
    const parent=Object.entries(origins).find(([key,value])=>key!==id&&value.children.includes(id));
    if(!parent)return 0;
    const parentDepth=depth(parent[0],seen);
    return parentDepth===null?null:1+parentDepth;
  };
  const ranked=Object.keys(proof).map((id)=>({id,depth:depth(id)}));
  if(ranked.some((entry)=>entry.depth===null))return false;
  for(const {id:originId} of ranked.sort((a,b)=>b.depth!-a.depth!)){
    const origin=origins[originId]!;
    for(const entry of proof[originId]!.slice().sort((a,b)=>a.sourceFrom-b.sourceFrom)){
    const retained=items[entry.fragmentId];
    const sourceCopies=Object.values(graph.items).filter((item)=>
      item.type==="group"&&(item.id===originId||item.timelineSourceItemId===origin.lineageId));
    if(sourceCopies.length!==1)return false;
    const pastedId=itemIds.get(sourceCopies[0].id);
    const pasted=pastedId?items[pastedId]:null;
    if(retained?.type!=="group"||pasted?.type!=="group")return false;
    const retainedBefore=before.undoableState.items[entry.fragmentId];
    const expectedFrom=retainedBefore&&retainedBefore.from>=insertionAt
      ?retainedBefore.from+insertionDuration:retainedBefore?.from;
    if(retainedBefore?.type!=="group"||
      retained.from!==expectedFrom||
      retained.durationInFrames!==retainedBefore.durationInFrames)return false;
    const pastedEnd=pasted.from+pasted.durationInFrames;
    const retainedEnd=retained.from+retained.durationInFrames;
    const overlaps=pasted.from<retainedEnd&&retained.from<pastedEnd;
    if(!overlaps)continue;
    // A preserved group frame can extend past its copied children. An empty
    // envelope cannot double-composite the independently retained fragment.
    if(!groupContentOverlaps(pasted.id,items,retained.from,retainedEnd))continue;
    if(retained.from!==entry.sourceFrom+pasteOffset||retained.durationInFrames!==entry.sourceDuration)return false;
    const beforeInsertion=retainedEnd<=insertionAt;
    const afterInsertion=retained.from>=insertionAt+insertionDuration;
    if((!beforeInsertion&&!afterInsertion)||retained.from<pasted.from||retainedEnd>pastedEnd||
      !compatibleRetainedPresentation(sourceCopies[0] as GroupItem,retainedBefore,entry.sourceFrom,graph.fps))return false;
    if(Object.values(transitions).some((transition)=>
      transition.sourceItemId===retained.id||transition.targetItemId===retained.id))return false;
    const children=[...new Set([...pasted.children,...retained.children])];
    const rank=(id:string)=>groupChildRank(id,origin.children,origin.childLineages,items);
    if(children.some((id)=>rank(id)<0))return false;
    for(let i=0;i<children.length;i++)for(let j=i+1;j<children.length;j++){
      const a=items[children[i]]!,b=items[children[j]]!;
      if(rank(a.id)===rank(b.id)&&a.from<b.from+b.durationInFrames&&b.from<a.from+a.durationInFrames)return false;
    }
    children.sort((a,b)=>rank(a)-rank(b)||items[a]!.from-items[b]!.from);
    items[pasted.id]={...pasted,children};
    for(const [id,item] of Object.entries(items))if(id!==retained.id&&item.type==="group"&&item.children.includes(retained.id))
      items[id]={...item,children:[...new Set(item.children.map((child)=>child===retained.id?pasted.id:child))]};
    for(let i=0;i<tracks.length;i++)if(tracks[i].items.includes(retained.id))
      tracks[i]={...tracks[i],items:tracks[i].items.filter((id)=>id!==retained.id)};
    delete items[retained.id];
    }
  }
  return true;
}

/** Original media may be reused; every editable clip tree and caption document gets independent identities. */
export function pasteClipboardGraph(
  state: EditorState,
  graph: ClipboardGraph,
  from: number,
  position: null | { x: number; y: number } = null,
  newTracks = false,
): EditorState {
  if (
    !parseClipboardGraph(graph) ||
    !frame(from) ||
    graph.fps !== state.undoableState.fps ||
    (position && (!number(position.x) || !number(position.y)))
  )
    return state;
  const baseFrom = Math.min(...graph.roots.map((id) => graph.items[id]!.from));
  const end = Math.max(
    ...graph.roots.map(
      (id) => graph.items[id]!.from + graph.items[id]!.durationInFrames,
    ),
  );
  const offset = from - baseFrom;
  const reserved = new Set([
    ...Object.keys(state.undoableState.items),
    ...Object.keys(state.undoableState.assets),
    ...state.undoableState.tracks.map((t) => t.id),
    ...Object.keys(state.undoableState.transitions),
  ]);
  let serial = 0;
  const fresh = (prefix: string) => {
    let id = prefix + generateRandomId(12);
    while (reserved.has(id)) id = prefix + ++serial;
    reserved.add(id);
    return id;
  };
  const itemIds = new Map(
    Object.keys(graph.items).map((id) => [id, fresh("clip-")]),
  );
  const assetIds = new Map<string, string>();
  let next = state;
  // Only a time-line paste onto an existing main track initiates global insertion.
  const targets = new Map<string, string>();
  const category = (t: TrackType) =>
    t.zone === "audio"
      ? "audio"
      : t.metadata?.type === "caption" || t.metadata?.type === "captions"
        ? "caption"
        : "video";
  const reusable = graph.tracks.filter(
    (t) =>
      !newTracks &&
      !position &&
      state.undoableState.tracks.some(
        (existing) =>
          existing.id === t.id &&
          !existing.locked &&
          category(existing) === category(t),
      ),
  );
  const magnetic = reusable.filter(
    (t) =>
      state.undoableState.tracks.find((existing) => existing.id === t.id)!
        .magnetic,
  );
  const global =
    state.isLinkingEnabled &&
    magnetic.some((t) => {
      const target = state.undoableState.tracks.find(
        (existing) => existing.id === t.id,
      )!;
      return target.metadata?.isMainTrack && target.metadata.type === "video";
    });
  const insertionFrom = graph.mixedMainInsertionRange
      ? from + graph.mixedMainInsertionRange.start - baseFrom
      : from;
  const insertionDuration = graph.mixedMainInsertionRange
      ? graph.mixedMainInsertionRange.end - graph.mixedMainInsertionRange.start
      : end - baseFrom;
  if (magnetic.length) {
    const result = insertTimelineTime(
      state,
      global
        ? state.undoableState.tracks.filter((t) => !t.locked).map((t) => t.id)
        : magnetic.map((t) => t.id),
      insertionFrom,
      insertionDuration,
      [],
      { crossingVisuals: "split" },
    );
    if (!result.ok) return state;
    next = result.state;
  }
  const items = { ...next.undoableState.items },
    assets = { ...next.undoableState.assets };
  for (const [id, asset] of Object.entries(graph.assets)) {
    const existing = assets[id];
    const reusableAsset =
      asset.type !== "caption" &&
      existing &&
      JSON.stringify(existing) === JSON.stringify(asset);
    const target = reusableAsset ? id : fresh("asset-");
    assetIds.set(id, target);
    if (!reusableAsset)
      assets[target] = { ...structuredClone(asset), id: target };
  }
  const originals = graph.roots.map((id) => graph.items[id]!);
  const left = Math.min(...originals.map((i) => i.left)),
    top = Math.min(...originals.map((i) => i.top));
  const right = Math.max(...originals.map((i) => i.left + i.width)),
    bottom = Math.max(...originals.map((i) => i.top + i.height));
  const dx = position ? position.x - (left + right) / 2 : 0,
    dy = position ? position.y - (top + bottom) / 2 : 0;
  const legacyGroups = new Map<string, string>();
  const copiedScenes=new Map<string,string>();
  const copiedLineages=new Map<string,string>(),copiedLines=new Map<string,string>();
  for(const source of Object.values(graph.items)){const lineage=source.timelineSourceItemId??source.id;if(!copiedLineages.has(lineage))copiedLineages.set(lineage,itemIds.get(source.id)!);for(const line of [...source.sourceScript?.lines??[],...source.sourceScript?.narrationLines??[]])if(!copiedLines.has(line.id))copiedLines.set(line.id,fresh('source-line-'));}
  for(const source of Object.values(graph.items))if(source.sourceScene?.anchor&&!copiedScenes.has(source.sourceScene.id)&&Object.values(next.undoableState.items).some(i=>i.sourceScene?.anchor&&i.sourceScene.id===source.sourceScene!.id))copiedScenes.set(source.sourceScene.id,fresh('source-scene-'));
  const castNames=new Map<string,string>();
  const generationCast=[...next.undoableState.sourceSceneGeneration?.cast??[]];
  for(const cast of graph.sourceSceneGeneration?.cast??[]){
    const mainAssetId=assetIds.get(cast.mainAssetId);if(!mainAssetId)return state;
    const existing=generationCast.find(c=>c.name===cast.name);
    let name=cast.name;
    if(existing&&existing.mainAssetId!==mainAssetId){let n=1;do{name=cast.name+' (복사 '+n+++')';}while(generationCast.some(c=>c.name===name));}
    castNames.set(cast.name,name);if(!generationCast.some(c=>c.name===name))generationCast.push({name,mainAssetId});
  }
  for (const [id, source] of Object.entries(graph.items)) {
    const item = {
      ...structuredClone(source),
      id: itemIds.get(id)!,
      timelineSourceItemId: source.sourceScene||source.sourceScript?copiedLineages.get(source.timelineSourceItemId??source.id)!:source.timelineSourceItemId ?? source.id,
      from: source.from + offset,
      left: source.left + dx,
      top: source.top + dy,
      isDraggingInTimeline: false,
    };
    if(item.sourceBrandIntro)delete item.sourceBrandIntro.leadingInterval;
    if(item.sourceSlide){const data=item.sourceSlide;item.sourceSlide={...data,beatSources:data.beatSources.map(beat=>({...beat,sourceLineId:copiedLines.get(beat.sourceLineId)??beat.sourceLineId})),...(data.media?{media:{...data.media,assetId:assetIds.get(data.media.assetId)!}}:{}),context:{...data.context,previousItemId:data.context.previousItemId?itemIds.get(data.context.previousItemId):undefined},provenance:{...data.provenance,sceneId:copiedScenes.get(data.provenance.sceneId)??data.provenance.sceneId}};}
    if(item.sourceScene&&copiedScenes.has(item.sourceScene.id))item.sourceScene={...item.sourceScene,id:copiedScenes.get(item.sourceScene.id)!};
    if(item.sourceSceneSpec){
      const spec=item.sourceSceneSpec,sourceLineage=source.timelineSourceItemId??source.id;
      const fallback=Object.values(graph.items).find(candidate=>candidate.sourceScene?.anchor&&candidate.sourceScene.id===spec.sceneId&&(candidate.timelineSourceItemId??candidate.id)===sourceLineage);
      const owner=itemIds.get(spec.ownerItemId)??(fallback?itemIds.get(fallback.id):undefined);
      item.sourceSceneSpec={...spec,sceneId:copiedScenes.get(spec.sceneId)??spec.sceneId,ownerItemId:owner??spec.ownerItemId,...(spec.sourceReferences?{sourceReferences:spec.sourceReferences.map(value=>({...value,...(value.assetId?{assetId:assetIds.get(value.assetId)??value.assetId}: {})}))}:{}),...(spec.capsule?{capsule:{...spec.capsule,...(spec.capsule.startAnchor?.castName?{startAnchor:{...spec.capsule.startAnchor,castName:castNames.get(spec.capsule.startAnchor.castName)??spec.capsule.startAnchor.castName}}:{})}}:{})};
    }
    // Copies own new lines and bindings; whole-document restore receipts must never cross a paste.
    delete item.sourceSpeechRestore;
    if(item.sourceScript){const script=item.sourceScript;const lineId=(id:string)=>copiedLines.get(id)!;item.sourceScript={...script,lines:script.lines.map(line=>({...line,id:lineId(line.id),bindings:line.bindings.flatMap(binding=>{const mapped=copiedLineages.get(binding.lineage);return mapped?[{...binding,lineage:mapped}]:[];})})),...(script.narrationLines?{narrationLines:script.narrationLines.map(line=>({...line,id:lineId(line.id)}))}:{})};}
    if (item.type === "group")
      item.children = item.children.map((child) => itemIds.get(child)!);
    if ("assetId" in item) item.assetId = assetIds.get(item.assetId)!;
    if (item.type === "captions" && item.captionPresentationNextItemId)
      item.captionPresentationNextItemId = itemIds.get(
        item.captionPresentationNextItemId,
      );
    if (item.groupId) {
      if (!legacyGroups.has(item.groupId))
        legacyGroups.set(item.groupId, fresh("group-"));
      item.groupId = legacyGroups.get(item.groupId);
    }
    items[item.id] = item;
  }
  const tracks = [...next.undoableState.tracks];
  for (const source of graph.tracks) {
    const added = source.items.map((id) => itemIds.get(id)!);
    const index = tracks.findIndex((t) => t.id === source.id && !t.locked);
    const canReuse =
      reusable.some((t) => t.id === source.id) &&
      index >= 0 &&
      !added.some((id) =>
        tracks[index]!.items.some((other) => {
          const a = items[id]!,
            b = items[other]!;
          return (
            a.from < b.from + b.durationInFrames &&
            b.from < a.from + a.durationInFrames
          );
        }),
      );
    if (canReuse) {
      const t = tracks[index]!;
      targets.set(source.id, t.id);
      tracks[index] = {
        ...t,
        items: orderTrackBlocks([...t.items, ...added], items),
      };
    } else {
      const id = fresh("track-");
      targets.set(source.id, id);
      const track = {
        ...structuredClone(source),
        id,
        items: added,
        locked: false,
        metadata: source.metadata
          ? { ...source.metadata, isMainTrack: false, trackType: null }
          : undefined,
      };
      const firstAudio = tracks.findIndex((t) => t.zone === "audio");
      const at =
        index >= 0
          ? index
          : source.zone === "audio"
            ? tracks.length
            : firstAudio < 0
              ? tracks.length
              : firstAudio;
      tracks.splice(at, 0, track);
    }
  }
  const transitions = { ...next.undoableState.transitions };
  for (const source of Object.values(graph.transitions)) {
    const id = fresh("transition-");
    transitions[id] = {
      ...structuredClone(source),
      id,
      sourceItemId: itemIds.get(source.sourceItemId)!,
      targetItemId: itemIds.get(source.targetItemId)!,
      trackId: targets.get(source.trackId)!,
      from: source.from + offset,
    };
  }
  if(magnetic.length&&!newTracks&&!position&&
    !coalesceProvenCutGroups(state,graph,itemIds,items,tracks,transitions,insertionFrom,insertionDuration,offset))return state;
  if(graph.mixedCutGroupProof){
    const retained=Object.values(graph.mixedCutGroupProof).flat().map((entry)=>entry.fragmentId)
      .filter((id)=>items[id]);
    if(!hasUniqueItemOwnership(items,tracks,[...itemIds.values(),...retained]))return state;
  }
  if(!validateSourceSceneGenerationDocumentStructure({...next.undoableState,items,assets,tracks,...(generationCast.length?{sourceSceneGeneration:{version:1,cast:generationCast}}:{})}))return state;
  return {
    ...next,
    timelineTimeEditApplied: true,
    selectedItems: graph.roots.map((id) => itemIds.get(id)!),
    undoableState: {
      ...next.undoableState,
      items,
      assets,
      ...(generationCast.length?{sourceSceneGeneration:{version:1 as const,cast:generationCast}}:{}),
      tracks,
      transitions,
    },
  };
}
